import { useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, LinearProgress, Typography } from '@mui/material';
import { CloudUploadOutlined } from '@mui/icons-material';
import { toast } from 'sonner';
import { colors } from 'Constants/Colors';
import useTranslation from 'Hooks/useTranslation';
import {
  EVIDENCE_ALLOWED_MIME,
  EVIDENCE_MAX_PER_SESSION,
  validateEvidence,
} from '../utils/evidence-validation';

const styles = {
  wrap: {
    border: `2px dashed ${colors.GREEN}66`,
    borderRadius: 2,
    p: 2,
    textAlign: 'center',
    bgcolor: `${colors.GREEN_ULTRA_LIGHT}`,
    cursor: 'pointer',
    transition: 'background 0.15s',
    '&:hover': { bgcolor: colors.GREEN_ULTRA_LIGHT },
  },
  cta: { fontSize: 13.5, fontWeight: 600, color: colors.BLACK, mt: 0.5 },
  hint: { fontSize: 11.5, color: colors.GRAY_DARK, mt: 0.25 },
  disabled: { opacity: 0.5, pointerEvents: 'none' },
};

/**
 * File picker for session evidences. Single responsibility: pick a file,
 * validate it client-side, hand it to `onUpload`. The parent owns the network
 * call and progress state; this component only renders the affordance.
 */
export default function EvidenceUploader({
  onUpload,
  currentCount,
  uploadProgress,
  disabled,
}) {
  const { t2 } = useTranslation('EditCourseSessions');
  const inputRef = useRef(null);

  const handlePick = e => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so the same file can be re-picked
    if (!file) return;
    const error = validateEvidence(file, currentCount);
    if (error === 'tooLarge') {
      toast.error(t2('errors.fileTooLarge'));
      return;
    }
    if (error === 'badType') {
      toast.error(t2('errors.fileTypeInvalid'));
      return;
    }
    if (error === 'limitReached') {
      toast.error(t2('errors.fileLimitReached'));
      return;
    }
    onUpload(file);
  };

  const limitReached = currentCount >= EVIDENCE_MAX_PER_SESSION;
  const isBusy = !!uploadProgress;

  return (
    <Box>
      <Box
        component="label"
        sx={{
          ...styles.wrap,
          ...(disabled || limitReached || isBusy ? styles.disabled : {}),
          display: 'block',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={EVIDENCE_ALLOWED_MIME.join(',')}
          hidden
          disabled={disabled || limitReached || isBusy}
          onChange={handlePick}
        />
        <CloudUploadOutlined sx={{ fontSize: 28, color: colors.GREEN }} />
        <Typography sx={styles.cta}>{t2('evidences.upload')}</Typography>
        <Typography sx={styles.hint}>{t2('evidences.limits')}</Typography>
        <Typography sx={styles.hint}>
          {currentCount}/{EVIDENCE_MAX_PER_SESSION}
        </Typography>
      </Box>

      {uploadProgress && (
        <Box sx={{ mt: 1.5 }}>
          <Typography sx={{ fontSize: 12, color: colors.GRAY_DARKER, mb: 0.5 }}>
            {t2('evidences.uploading', [uploadProgress.name])}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={uploadProgress.percent}
            sx={{ height: 6, borderRadius: 99, bgcolor: colors.BLUE_GRAY }}
          />
        </Box>
      )}
    </Box>
  );
}

EvidenceUploader.propTypes = {
  onUpload: PropTypes.func.isRequired,
  currentCount: PropTypes.number,
  uploadProgress: PropTypes.shape({ name: PropTypes.string, percent: PropTypes.number }),
  disabled: PropTypes.bool,
};

EvidenceUploader.defaultProps = { currentCount: 0, uploadProgress: null, disabled: false };
